import { If, onMount, reactive, location } from 'cdui-js';
import { DiFiNavBar } from '../../../components/DiFi/DiFiNavBar';
import i18n from '../../../i18n';
import { goBackByBrowser } from '../../../lib/browser';
import { isLegalEmbeddedInApp } from '../../legal/legalEmbed';
import { isHelpArticleId, resolveHelpArticleHtmlUrl } from './articles';

type HelpCenterState = {
  loading: boolean;
  title: string;
  bodyHtml: string;
  placeholder: string;
  error: string;
};

function resolveArticleTitle(articleId: string): string {
  const articles = i18n.HelpCenter?.articles as Record<string, string> | undefined;
  return articles?.[articleId] ?? articleId;
}

export const HelpCenterPage = () => {
  const embedded = isLegalEmbeddedInApp();
  const t = i18n.HelpCenter;
  const query = location.query as Record<string, string | undefined>;
  const articleId = query.article?.trim() ?? '';

  const state = reactive<HelpCenterState>({
    loading: true,
    title: resolveArticleTitle(articleId),
    bodyHtml: '',
    placeholder: t?.ContentComingSoon ?? '',
    error: '',
  });

  onMount(async () => {
    if (!isHelpArticleId(articleId)) {
      state.loading = false;
      state.error = t?.LoadFailed ?? '';
      return;
    }

    state.title = resolveArticleTitle(articleId);

    try {
      const res = await fetch(resolveHelpArticleHtmlUrl(articleId));
      if (res.ok) {
        const html = (await res.text()).trim();
        if (html) {
          state.bodyHtml = html;
        }
      }
    } catch {
      // 无远端 HTML 时降级为占位文案
    } finally {
      state.loading = false;
    }
  });

  const pageClass = embedded
    ? 'help-center-page page-wrapper-no-header flex-column bg-c-D'
    : 'help-center-page page-wrapper-with-header flex-column bg-c-D';

  return (
    <div class={pageClass}>
      <If when={!embedded}>
        <DiFiNavBar title={state.title} fixed class="bg-c" onClickLeft={() => goBackByBrowser()} />
      </If>

      <div
        class="flex-1 no-scrollbar padding-x padding-b-L"
        style={{ 'overflow-y': 'auto', 'padding-top': embedded ? '16px' : '54px' }}
      >
        <If when={state.loading}>
          <div class="font-s-S color margin-t">加载中...</div>
        </If>

        <If when={!state.loading && state.error}>
          <div class="font-s-S color-error margin-t">{state.error}</div>
        </If>

        <If when={!state.loading && !state.error && state.bodyHtml}>
          <div
            class="help-center-html font-s-S color-D"
            style={{ 'line-height': '22px' }}
            innerHTML={state.bodyHtml}
          />
        </If>

        <If when={!state.loading && !state.error && !state.bodyHtml}>
          <div class="font-s-L color-D font-bold margin-b-XS" style={{ 'line-height': '26px' }}>
            {state.title}
          </div>
          <div class="font-s-S color" style={{ 'line-height': '22px', 'white-space': 'pre-wrap' }}>
            {state.placeholder}
          </div>
        </If>
      </div>
    </div>
  );
};

export default HelpCenterPage;
